import { useAnalytics } from "@dgs/core";
import { useHead } from "@unhead/react";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useConfig } from "../shared/hooks/useConfig";
import { AllAnalyticsEvents } from "./analyticsEvents";

type EventTypes = "page.ready" | "form.open" | "form.sent" | "newsletter.optout";

declare global {
	interface Window {
		ste_statistic: {
			push(event: Record<string, unknown>): void;
		};
	}
}

window.ste_statistic = window.ste_statistic || [];

interface Props {
	children: ReactNode;
}

export const AdobeAnalytics: FC<Props> = ({ children }) => {
	const { languageSettings, productData } = useConfig();
	const { listen } = useAnalytics();
	const [initialized, setInitialized] = useState(false);

	useHead({
		script: productData.adobeAnalyticsThirdPartyScripts || [],
	});

	const trackEvent = useCallback((event: EventTypes, data: Record<string, any>) => {
		window.ste_statistic.push({
			action: event,
			data: {
				...data,
			},
		});
	}, []);

	useEffect(() => {
		setInitialized(true);
		const unsubscribe = listen((event: AllAnalyticsEvents) => {
			switch (event.type) {
				case "PAGE_VIEWED":
					trackEvent("page.ready", {
						page: {
							country: "WW",
							language: languageSettings.fallback,
							template: "DGS",
							title: event.payload.pageUrl,
						},
					});
					break;
				case "REGISTRATION_PROCESS_STARTED":
					trackEvent("form.open", {
						form: {
							type: "eventregistration",
							recipient: "general",
						},
					});
					break;
				case "REGISTRATION_PROCESS_COMPLETED":
					trackEvent("form.sent", {
						form: {
							type: "eventregistration",
							recipient: "general",
						},
					});
					break;
				case "REGISTRATION_CANCELED":
				case "REGISTRATION_DECLINED":
				case "REGISTRATION_REPLACEMENT":
					trackEvent("newsletter.optout", {
						newsletter: {
							recipient: productData.htmlTitle,
						},
					});
					break;
			}
		});

		return () => {
			unsubscribe();
		};
	}, [listen, trackEvent, productData.htmlTitle, languageSettings.fallback]);

	if (productData.adobeAnalyticsThirdPartyScripts === null) {
		return null;
	}

	return initialized ? children : null;
};
